import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ServerLeaderboardService } from '../../../services/ServerLeaderboardService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { RESTAURANT_BACKEND_HOST, apiCall } from '../RestaurantBackend';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import {
  getShiftTranscript,
  getShiftInteractions,
  getTenantServers,
  ALL_TENANTS,
} from '../../../services/scopedReadsService';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FullPageSpinner from '../../FullPageSpinner';
import ErrorMessage from '../../common/ErrorMessage';
import { useApiError } from '../../../hooks/useApiError';
import { computeScore } from '../../../utils/scoreUtils';
import * as Sentry from '@sentry/react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Server {
  id: string;
  name: string;
}

interface DailyStats {
  date: string;
  initial_greeting_count: number;
  criteria_stats: {
    upsell_count: number;
    mention_count: number;
    describe_count: number;
    drink_promotion_count: number;
  };
  premium_stats: {
    regular_sales: number;
    premium_sales: number;
    premium_conversion_rate: number;
  };
  shift_start_time: string;
  shift_end_time: string | null;
  duration_minutes: number;
  shifts: Array<{
    shift_id: string;
    shift_created: string;
    shift_ended: string | null;
    duration_minutes: number;
    interactions_count: number;
    fragments_count: number;
  }>;
}

const ServerPerformanceAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  const searchParams = new URLSearchParams(location.search);

  // Get parameters from URL
  const urlServerId = searchParams.get('serverId');
  const urlStartDate = searchParams.get('startDate');
  const urlEndDate = searchParams.get('endDate');
  const isAdvanced = searchParams.get('advanced') === 'true';
  const useBackend = searchParams.get('useBackend') === 'true';

  const [servers, setServers] = useState<Server[]>([]);
  const [selectedServer, setSelectedServer] = useState<string>(urlServerId || '');
  const [startDate, setStartDate] = useState<string>(urlStartDate || '2025-03-01');
  const [endDate, setEndDate] = useState<string>(urlEndDate || format(new Date(), 'yyyy-MM-dd'));
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [serversError, setServersError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Add new state variables for interactions
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [allInteractions, setAllInteractions] = useState<any[]>([]);
  const [showAllInteractions, setShowAllInteractions] = useState(false);

  // Error handling
  const { error: apiError, handleError: handleApiError, clearError: clearApiError } = useApiError();

  // Update URL when filters change
  useEffect(() => {
    if (selectedServer && startDate && endDate) {
      const params = new URLSearchParams();
      params.set('serverId', selectedServer);
      params.set('startDate', startDate);
      params.set('endDate', endDate);

      // Preserve the advanced parameter if it exists
      if (isAdvanced) {
        params.set('advanced', 'true');
      }

      if (useBackend) {
        params.set('useBackend', 'true');
      }

      navigate(
        `/restaurant/servers/performance?${params.toString()}`,
        { replace: true }
      );
    }
  }, [selectedServer, startDate, endDate, navigate, isAdvanced, useBackend]);

  useEffect(() => {
    fetchServers();
  }, []);

  useEffect(() => {
    if (selectedServer) {
      fetchServerStats();
    }
  }, [selectedServer, startDate, endDate]);

  const fetchServers = async () => {
    try {
      setServersError(null);
      clearApiError();

      // feat-133 Batch-2: scoped ref-lookup replaces the unscoped execute-select
      // `SELECT DISTINCT s.id,s.name FROM server JOIN shift WHERE demo=FALSE AND
      // test=FALSE` — the exclude_demo_test + has_shifts flags reproduce it exactly
      // (Batch-1 endpoint). Manager/Corporate → own tenant (the fix); Admin retains
      // the global list via the `all` sentinel + ?all_tenants=true.
      const rawRole = user?.managementUser?.role;
      const role = rawRole
        ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase()
        : null;
      const serverOpts = { excludeDemoTest: true, hasShifts: true } as const;

      let results: Array<{ id: string; name: string }> = [];
      if (role === 'Admin') {
        results = await getTenantServers(ALL_TENANTS, { allTenants: true, ...serverOpts });
      } else {
        const tenantId = user?.managementUser?.tenant_id;
        if (tenantId) {
          results = await getTenantServers(tenantId, serverOpts);
        }
      }
      setServers(results.map((s) => ({ id: s.id, name: s.name })));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred while fetching servers';
      setServersError(errorMessage);
      handleApiError(error);
      Sentry.captureException(error);
      console.error('Error fetching servers:', error);
    }
  };

  const fetchServerStats = async () => {
    setIsLoading(true);
    try {
      setStatsError(null);
      clearApiError();
      
      let stats: any[] = [];
      if (useBackend) {
        // Use the backend endpoint
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          group_by_date: 'true',
          server_ids: selectedServer,
        });
        const response = await apiCall(`/admin/server-performance?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch server stats from backend: ${response.status} ${errorText}`);
        }
        const data = await response.json();
        stats = data.results || [];
      } else {
        // Use the old method. feat-133 Batch-3: pass tenant from auth context —
        // the aggregation is now tenant-scoped server-side.
        const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;
        stats = await ServerLeaderboardService.getServerLeaderboardData(
          startDate,
          endDate,
          [selectedServer],
          true,
          undefined,
          false,
          tenantId
        );
      }

      // Filter out data points where initial_greeting_count <= 1
      const filteredStats = (stats as any[])
        .filter(stat => stat.initial_greeting_count > 1)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      console.log('filteredStats', filteredStats);

      // Add shift timing information to each stat
      const statsWithTiming = filteredStats.map(stat => ({
        ...stat,
        shift_start_time: stat.shifts?.[0]?.shift_created || '',
        shift_end_time: stat.shifts?.[0]?.shift_ended || null,
        duration_minutes: stat.shifts?.[0]?.duration_minutes || 0
      }));

      setDailyStats(statsWithTiming);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred while fetching server statistics';
      setStatsError(errorMessage);
      handleApiError(error);
      Sentry.captureException(error);
      console.error('Error fetching server stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateScore = (stats: DailyStats): number => {
    if (stats.initial_greeting_count === 0) return 0;

    const upsellPercent = Math.round((stats.criteria_stats.upsell_count / stats.initial_greeting_count) * 100);
    const mentionPercent = Math.round((stats.criteria_stats.mention_count / stats.initial_greeting_count) * 100);
    const describePercent = Math.round((stats.criteria_stats.describe_count / stats.initial_greeting_count) * 100);
    const drinkPercent = Math.round((stats.criteria_stats.drink_promotion_count / stats.initial_greeting_count) * 100);

    return computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);
  };

  const calculateServerScore = (stats: DailyStats): number => {
    if (stats.initial_greeting_count === 0) return 0;

    const upsellPercent = Math.round((stats.criteria_stats.upsell_count / stats.initial_greeting_count) * 100);
    const mentionPercent = Math.round((stats.criteria_stats.mention_count / stats.initial_greeting_count) * 100);
    const describePercent = Math.round((stats.criteria_stats.describe_count / stats.initial_greeting_count) * 100);
    const drinkPercent = Math.round((stats.criteria_stats.drink_promotion_count / stats.initial_greeting_count) * 100);

    return computeScore([upsellPercent, mentionPercent, describePercent], drinkPercent);
  };

  const getCriteriaChartData = (criteriaKey: keyof DailyStats['criteria_stats']) => ({
    labels: dailyStats.map(stat => format(parseISO(stat.date), 'MMM d')),
    datasets: [{
      data: dailyStats.map(stat =>
        (stat.criteria_stats[criteriaKey] / stat.initial_greeting_count) * 100
      ),
      borderColor: {
        'upsell_count': 'rgb(255, 99, 132)',
        'mention_count': 'rgb(54, 162, 235)',
        'describe_count': 'rgb(255, 206, 86)',
        'drink_promotion_count': 'rgb(153, 102, 255)',
      }[criteriaKey],
      backgroundColor: {
        'upsell_count': 'rgba(255, 99, 132, 0.5)',
        'mention_count': 'rgba(54, 162, 235, 0.5)',
        'describe_count': 'rgba(255, 206, 86, 0.5)',
        'drink_promotion_count': 'rgba(153, 102, 255, 0.5)',
      }[criteriaKey],
      tension: 0.4,
    }],
  });

  const miniChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        position: 'top' as const,
        font: {
          size: 14,
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function (this: any, value: string | number) {
            return `${value}%`;
          },
        },
      },
      x: {
        display: false,
      },
    },
  };

  // Create a separate options object for drink promotions that doesn't limit the y-axis
  const drinkPromotionsChartOptions = {
    ...miniChartOptions,
    scales: {
      ...miniChartOptions.scales,
      y: {
        ...miniChartOptions.scales.y,
        max: undefined, // Remove the max limit for drink promotions
      },
    },
    plugins: {
      ...miniChartOptions.plugins,
      title: {
        ...miniChartOptions.plugins.title,
        display: true,
      },
    },
  };

  const criteriaCharts = [
    { key: 'upsell_count' as const, title: 'Premium Menu %' },
    { key: 'mention_count' as const, title: 'Item Mentions %' },
    { key: 'describe_count' as const, title: 'Item Descriptions %' },
    { key: 'drink_promotion_count' as const, title: 'Drink Promotions %' },
  ];

  // Calculate aggregate metrics
  const aggregateMetrics = dailyStats.reduce((acc, stat) => {
    acc.totalGreetings += stat.initial_greeting_count;
    acc.totalUpsells += stat.criteria_stats.upsell_count;
    acc.totalMentions += stat.criteria_stats.mention_count;
    acc.totalDescriptions += stat.criteria_stats.describe_count;
    acc.totalDrinkPromotions += stat.criteria_stats.drink_promotion_count;
    acc.totalRegularSales += stat.premium_stats.regular_sales;
    acc.totalPremiumSales += stat.premium_stats.premium_sales;
    return acc;
  }, {
    totalGreetings: 0,
    totalUpsells: 0,
    totalMentions: 0,
    totalDescriptions: 0,
    totalDrinkPromotions: 0,
    totalRegularSales: 0,
    totalPremiumSales: 0,
  });

  // Calculate percentages
  const percentages = {
    upsell: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalUpsells / aggregateMetrics.totalGreetings) * 100 : 0,
    mention: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalMentions / aggregateMetrics.totalGreetings) * 100 : 0,
    description: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalDescriptions / aggregateMetrics.totalGreetings) * 100 : 0,
    drink: aggregateMetrics.totalGreetings > 0 ? (aggregateMetrics.totalDrinkPromotions / aggregateMetrics.totalGreetings) * 100 : 0,
    premium: aggregateMetrics.totalRegularSales > 0 ? (aggregateMetrics.totalPremiumSales / aggregateMetrics.totalRegularSales) * 100 : 0,
  };

  // Calculate average score
  const averageScore = (percentages.upsell + percentages.mention + percentages.description + percentages.drink) / 4;

  // Add this helper function near the top with other helper functions
  const formatDuration = (minutes: number): string => {
    if (minutes <= 0) return 'N/A';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Add function to get transcript text for an interaction
  const getTranscriptText = async (shiftId: string, startTime: number, endTime: number): Promise<string> => {
    try {
      // feat-133 Batch-2: scoped transcript-window replaces the execute-select
      // `SELECT text,speaker,start_time FROM transcript_part WHERE shift_id=.. AND
      // start_time BETWEEN start AND end`. Shift authorized server-side (404 on a
      // foreign shift); window %s-bound inclusive. Same rows → identical format.
      const parts = await getShiftTranscript(shiftId, { start: startTime, end: endTime });

      if (parts.length === 0) {
        return 'No transcript parts found for this time range';
      }

      // Format the transcript parts by speaker
      const formatted = parts
        .map((part) => `Speaker ${part.speaker}: ${part.text}`)
        .join('\n\n');

      return formatted;
    } catch (error) {
      console.error('Error fetching transcript text:', error);
      return 'Error loading transcript';
    }
  };

  // Helper function to format interaction type
  const formatInteractionType = (type: string): string => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Add function to load all interactions
  const loadAllInteractions = async () => {
    if (!selectedServer || dailyStats.length === 0) return;

    setLoadingInteractions(true);
    setAllInteractions([]);

    try {
      // Get all shift IDs from all daily stats
      const allShifts = dailyStats.flatMap(stat => stat.shifts || []);

      if (allShifts.length === 0) {
        console.log('No shifts found');
        return;
      }

      console.log(`Found ${allShifts.length} shifts to process`);

      // Get all interactions from all shifts
      const interactionsWithText = [];

      for (const shift of allShifts) {
        if (!shift.shift_id) {
          console.log('Shift has no ID, skipping');
          continue;
        }

        console.log(`Processing shift: ${shift.shift_id}`);

        // feat-133 Batch-2: scoped interactions replaces the execute-select
        // `SELECT ... FROM shift_interaction WHERE shift_id=..`. Shift authorized
        // server-side (404 on a foreign shift). The endpoint omits shift_id (known
        // from the loop), so re-attach it for the downstream getTranscriptText call.
        try {
          const rows = await getShiftInteractions(shift.shift_id);
          const shiftInteractions = rows.map((r) => ({ ...r, shift_id: shift.shift_id }));

          console.log(`Found ${shiftInteractions.length} interactions for shift ${shift.shift_id}`);

          // For each interaction, get the transcript text
          for (const interaction of shiftInteractions) {
            try {
              const transcriptText = await getTranscriptText(
                interaction.shift_id,
                interaction.start_time,
                interaction.end_time
              );

              interactionsWithText.push({
                ...interaction,
                transcriptText,
                shift_created: shift.shift_created,
                shift_ended: shift.shift_ended,
                date: new Date(shift.shift_created).toISOString().split('T')[0]
              });
            } catch (error) {
              console.error(`Error fetching transcript for interaction ${interaction.id}:`, error);

              // Still add the interaction but with an error message
              interactionsWithText.push({
                ...interaction,
                transcriptText: 'Error loading transcript text',
                shift_created: shift.shift_created,
                shift_ended: shift.shift_ended,
                date: new Date(shift.shift_created).toISOString().split('T')[0]
              });
            }
          }
        } catch (error) {
          console.error(`Error processing shift ${shift.shift_id}:`, error);
        }
      }

      // Sort interactions by date and start_time
      interactionsWithText.sort((a, b) => {
        if (a.date !== b.date) {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        return a.start_time - b.start_time;
      });

      console.log(`Total interactions loaded: ${interactionsWithText.length}`);
      setAllInteractions(interactionsWithText);
      setShowAllInteractions(true);
    } catch (error) {
      console.error('Error loading all interactions:', error);
    } finally {
      setLoadingInteractions(false);
    }
  };

  // Handle loading state
  if (isLoading) {
    return <FullPageSpinner />;
  }

  // Handle critical errors that prevent the page from loading
  if (apiError && !servers.length && !dailyStats.length) {
    return (
      <ErrorMessage
        title="Unable to Load Performance Analysis"
        message="We're having trouble connecting to the database. This could be due to network issues or server maintenance."
        details={apiError?.details}
        onRetry={() => {
          clearApiError();
          window.location.reload();
        }}
        onGoBack={() => navigate('/restaurant/servers/trial')}
      />
    );
  }

  // Handle servers error (show partial data if stats are available)
  if (serversError && !servers.length) {
    return (
      <ErrorMessage
        title="Unable to Load Server List"
        message="We couldn't load the server list, but you can still view performance data if a server is selected."
        details={serversError}
        onRetry={() => {
          setServersError(null);
          fetchServers();
        }}
        showGoBack={false}
      />
    );
  }

  // Handle stats error (show partial data if servers are available)
  if (statsError && !dailyStats.length && selectedServer) {
    return (
      <ErrorMessage
        title="Unable to Load Performance Data"
        message="We couldn't load the performance data for the selected server. This could be due to database connectivity issues."
        details={statsError}
        onRetry={() => {
          setStatsError(null);
          fetchServerStats();
        }}
        showGoBack={false}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">
        {selectedServer ? (
          <>
            {servers.find(s => s.id === selectedServer)?.name} Performance Analysis
            <div className="text-sm font-normal text-gray-500 mt-1">
              {format(parseISO(startDate), 'MMM d, yyyy')} - {format(parseISO(endDate), 'MMM d, yyyy')}
            </div>
          </>
        ) : (
          'Server Performance Analysis'
        )}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Server</label>
          <select
            value={selectedServer}
            onChange={(e) => setSelectedServer(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select a server</option>
            {servers.map((server) => (
              <option key={server.id} value={server.id}>
                {server.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Show warning if there are partial errors */}
      {(serversError || statsError) && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Partial Data Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                {serversError && <p>• Server list unavailable</p>}
                {statsError && <p>• Performance data unavailable</p>}
                <p className="mt-1">Some features may be limited. Try refreshing the page or selecting a different server.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <FullPageSpinner />
        </div>
      ) : dailyStats.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '200px' }}>
              <Line
                data={{
                  labels: dailyStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Greetings',
                    data: dailyStats.map(stat => stat.initial_greeting_count),
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.5)',
                    tension: 0.4,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      beginAtZero: true,
                      ticks: {
                        callback: function (this: any, value: string | number) {
                          return value;
                        },
                      },
                    },
                    x: {
                      display: false,
                    },
                  },
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Total Greetings',
                    },
                  },
                }}
              />
            </div>

            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '200px' }}>
              <Line
                data={{
                  labels: dailyStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Score',
                    data: dailyStats.map(stat => calculateServerScore(stat)),
                    borderColor: 'rgb(255, 159, 64)',
                    backgroundColor: 'rgba(255, 159, 64, 0.5)',
                    tension: 0.4,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Score (%)',
                    },
                  },
                }}
              />
            </div>

            {criteriaCharts.map(({ key, title }) => (
              <div key={key} className="bg-white p-4 rounded-lg shadow" style={{ height: '200px' }}>
                <Line
                  data={getCriteriaChartData(key)}
                  options={{
                    ...(key === 'drink_promotion_count' ? drinkPromotionsChartOptions : miniChartOptions),
                    plugins: {
                      ...(key === 'drink_promotion_count' ? drinkPromotionsChartOptions : miniChartOptions).plugins,
                      title: {
                        ...miniChartOptions.plugins.title,
                        text: title,
                        display: true,
                      },
                    },
                  }}
                />
              </div>
            ))}

            {/* Add Total Sales Chart */}
            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '200px' }}>
              <Line
                data={{
                  labels: dailyStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Regular Sales',
                    data: dailyStats.map(stat => stat.premium_stats.regular_sales),
                    borderColor: 'rgb(54, 162, 235)',
                    backgroundColor: 'rgba(54, 162, 235, 0.5)',
                    tension: 0.4,
                  }, {
                    label: 'Premium Sales',
                    data: dailyStats.map(stat => stat.premium_stats.premium_sales),
                    borderColor: 'rgb(153, 102, 255)',
                    backgroundColor: 'rgba(153, 102, 255, 0.5)',
                    tension: 0.4,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  scales: {
                    y: {
                      type: 'linear' as const,
                      beginAtZero: true,
                      stacked: false,
                      ticks: {
                        callback: function (this: any, value: string | number) {
                          return value;
                        },
                      },
                    },
                    x: {
                      display: false,
                    },
                  },
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Total Sales',
                      display: true,
                    },
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        font: {
                          size: 10
                        }
                      }
                    }
                  },
                }}
              />
            </div>

            {/* Add Premium Conversion Rate Chart */}
            <div className="bg-white p-4 rounded-lg shadow" style={{ height: '200px' }}>
              <Line
                data={{
                  labels: dailyStats.map(stat => format(parseISO(stat.date), 'MMM d')),
                  datasets: [{
                    label: 'Premium Conversion Rate',
                    data: dailyStats.map(stat => stat.premium_stats.premium_conversion_rate),
                    borderColor: 'rgb(255, 159, 64)',
                    backgroundColor: 'rgba(255, 159, 64, 0.5)',
                    tension: 0.4,
                  }],
                }}
                options={{
                  ...miniChartOptions,
                  plugins: {
                    ...miniChartOptions.plugins,
                    title: {
                      ...miniChartOptions.plugins.title,
                      text: 'Premium Conversion Rate (%)',
                      display: true,
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Aggregate Performance Section */}
          <div className="mt-8 bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Aggregate Performance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Key Metrics</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Greetings</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalGreetings}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Average Score</span>
                      <span className="text-sm font-medium">{Math.round(averageScore)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(averageScore)}%`,
                          backgroundColor: 'rgb(255, 159, 64)' // Match Score chart color
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Regular Sales</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalRegularSales}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Premium Sales</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalPremiumSales}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Total Sales</span>
                      <span className="text-sm font-medium">{aggregateMetrics.totalRegularSales + aggregateMetrics.totalPremiumSales}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Premium Conversion Rate</span>
                      <span className="text-sm font-medium">{Math.round(percentages.premium)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.premium)}%`,
                          backgroundColor: 'rgb(75, 192, 192)'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Criteria Performance</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Premium Menu</span>
                      <span className="text-sm font-medium">{Math.round(percentages.upsell)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.upsell)}%`,
                          backgroundColor: 'rgb(255, 99, 132)' // Match upsell chart color
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Item Mentions</span>
                      <span className="text-sm font-medium">{Math.round(percentages.mention)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.mention)}%`,
                          backgroundColor: 'rgb(54, 162, 235)' // Match mentions chart color
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Item Descriptions</span>
                      <span className="text-sm font-medium">{Math.round(percentages.description)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.description)}%`,
                          backgroundColor: 'rgb(255, 206, 86)' // Match descriptions chart color
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Drink Promotions</span>
                      <span className="text-sm font-medium">{Math.round(percentages.drink)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round(percentages.drink)}%`,
                          backgroundColor: 'rgb(153, 102, 255)' // Match drink promotions chart color
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Only show shifts table in advanced mode */}
          {isAdvanced && (
            <>
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-bold mb-4">All Shifts</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duration
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Greetings
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Score
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dailyStats.map((stat, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {format(parseISO(stat.date), 'MMM d, yyyy')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stat.duration_minutes > 0 ? formatDuration(stat.duration_minutes) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {stat.initial_greeting_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {calculateScore(stat)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <Link
                              to={`/restaurant/shifts/${stat.shifts?.[0]?.shift_id}/transcript/${selectedServer}`}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              View Transcript
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add View All Interactions button */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={loadAllInteractions}
                  disabled={loadingInteractions}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition disabled:opacity-50"
                >
                  {loadingInteractions ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading Interactions...
                    </div>
                  ) : (
                    'View All Interactions'
                  )}
                </button>
              </div>

              {/* Display all interactions */}
              {showAllInteractions && (
                <div className="mt-6 bg-white p-6 rounded-lg shadow">
                  <h2 className="text-xl font-bold mb-4">All Interactions</h2>
                  <div className="space-y-6">
                    {allInteractions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No interactions found</div>
                    ) : (
                      allInteractions.map((interaction, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-sm">
                          <div className="bg-gray-50 p-4 border-b">
                            <div className="flex flex-wrap justify-between items-center gap-2">
                              <div>
                                <h3 className="font-medium text-lg">
                                  {formatInteractionType(interaction.interaction_type)}
                                </h3>
                                <p className="text-sm text-gray-500">
                                  {format(new Date(interaction.shift_created), 'MMM d, yyyy')} at {
                                    new Date(interaction.start_time * 1000).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  }
                                </p>
                              </div>
                              <div className="text-sm text-gray-500">
                                <span>Duration: {Math.round((interaction.end_time - interaction.start_time))} seconds</span>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <h4 className="font-medium text-sm mb-2 text-gray-700">Transcript:</h4>
                            <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded text-sm font-mono overflow-x-auto max-h-64 overflow-y-auto">
                              {interaction.transcriptText || 'No transcript available'}
                            </pre>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : selectedServer ? (
        <div className="text-center py-8 text-gray-500">No data available for the selected date range</div>
      ) : null}

      {/* Debugging Section */}
      {dailyStats.length > 0 && (
        <div className="mt-8 bg-gray-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Debugging Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded shadow">
              <div className="text-sm font-medium text-gray-500">Total Premium Mentions</div>
              <div className="text-xl font-bold">{aggregateMetrics.totalUpsells}</div>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <div className="text-sm font-medium text-gray-500">Total Item Mentions</div>
              <div className="text-xl font-bold">{aggregateMetrics.totalMentions}</div>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <div className="text-sm font-medium text-gray-500">Total Item Descriptions</div>
              <div className="text-xl font-bold">{aggregateMetrics.totalDescriptions}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerPerformanceAnalysis; 